import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Search, Filter, FileText, ChevronLeft, ChevronRight, RefreshCw,
  CheckCircle2, AlertTriangle, XCircle, Calendar, Store, Receipt,
  Package, CreditCard, Banknote, Clock, ArrowUpRight, TrendingUp,
  CalendarDays, List
} from 'lucide-react';
import { StorePeriodFilter, useStorePeriodFilter } from '@/components/retail/StorePeriodFilter';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format utilities
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT').format(value);
};

const formatDateTime = (date, time) => {
  if (!date) return 'N/D';
  const formattedDate = date.split('-').reverse().join('/');
  return time ? `${formattedDate} ${time}` : formattedDate;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/D';
  try {
    return format(new Date(dateStr), "EEEE, d 'de' MMMM", { locale: pt });
  } catch {
    return dateStr.split('-').reverse().join('/');
  }
};

// Reconciliation status badge
const ReconciliationBadge = ({ status }) => {
  const configs = {
    RECONCILED: { label: 'Reconciliado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    MISMATCH: { label: 'Diferença', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
    NO_PAYMENT: { label: 'Sem pagamento', color: 'bg-amber-100 text-amber-700', icon: XCircle },
    UNKNOWN_PAYMENT_CODE: { label: 'Código desconhecido', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
  };
  
  const config = configs[status] || configs.MISMATCH;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Document type badge
const DocumentTypeBadge = ({ type }) => {
  const configs = {
    FTS: { label: 'Venda', color: 'bg-emerald-100 text-emerald-700' },
    FTV: { label: 'Venda', color: 'bg-emerald-100 text-emerald-700' },
    FTN: { label: 'Venda', color: 'bg-emerald-100 text-emerald-700' },
    NCVS: { label: 'Devolução', color: 'bg-red-100 text-red-700' },
  };
  
  const config = configs[type] || { label: type, color: 'bg-slate-100 text-slate-700' };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Payment method badge
const PaymentMethodBadge = ({ method, code }) => {
  const configs = {
    CASH: { label: 'Numerário', color: 'bg-emerald-100 text-emerald-700', icon: Banknote },
    CARD: { label: 'Cartão', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
    OTHER: { label: code || 'Outro', color: 'bg-amber-100 text-amber-700', icon: Receipt },
  };
  
  const config = configs[method] || configs.OTHER;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Document detail modal
const DocumentDetailModal = ({ documentKey, open, onClose }) => {
  const { getStoreName } = useStoreMapping();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!open || !documentKey) return;
    
    const loadDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/retail-analytics/document/${encodeURIComponent(documentKey)}`);
        setDetail(res.data);
      } catch (err) {
        setError('Erro ao carregar detalhes do documento');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadDetail();
  }, [documentKey, open]);
  
  if (!open) return null;
  
  const doc = detail?.document;
  const lines = detail?.lines || [];
  const payments = detail?.payments || [];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documento {doc?.document_type} {doc?.raw_series}/{doc?.document_number}
          </DialogTitle>
          <DialogDescription>
            {getStoreName(doc?.store_code)} • {formatDateTime(doc?.business_date, doc?.business_time)}
          </DialogDescription>
        </DialogHeader>
        
        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded">{error}</div>
        )}
        
        {!loading && doc && (
          <div className="space-y-6">
            {/* Header info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs text-slate-500">Tipo</p>
                <DocumentTypeBadge type={doc.document_type} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Data/Hora</p>
                <p className="font-medium">{formatDateTime(doc.business_date, doc.business_time)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Documento</p>
                <p className="font-semibold text-lg">{formatCurrency(doc.net_amount)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Reconciliação</p>
                <ReconciliationBadge status={doc.reconciliation_status} />
              </div>
            </div>
            
            {/* Financial breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded">
                <p className="text-xs text-slate-500">Bruto</p>
                <p className="font-semibold">{formatCurrency(doc.gross_amount)}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded">
                <p className="text-xs text-slate-500">Descontos</p>
                <p className="font-semibold text-amber-700">-{formatCurrency(doc.discount_amount)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded">
                <p className="text-xs text-slate-500">IVA</p>
                <p className="font-semibold">{formatCurrency(doc.tax_amount)}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded">
                <p className="text-xs text-slate-500">Líquido</p>
                <p className="font-semibold text-emerald-700">{formatCurrency(doc.net_amount)}</p>
              </div>
            </div>
            
            {/* Source reference for NCVS */}
            {doc.document_type === 'NCVS' && (doc.source_document_type || doc.source_series) && (
              <div className="p-3 bg-blue-50 rounded flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  Referência original: {doc.source_document_type} {doc.source_series}/{doc.source_number}
                </span>
              </div>
            )}
            
            {/* Lines */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Linhas ({lines.length})
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Preço Unit.</TableHead>
                    <TableHead className="text-right">Desconto</TableHead>
                    <TableHead className="text-right">IVA</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">{line.sku}</TableCell>
                      <TableCell className="text-right">{line.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(line.unit_price)}</TableCell>
                      <TableCell className="text-right text-amber-600">
                        {line.discount_amount > 0 ? `-${formatCurrency(line.discount_amount)}` : '-'}
                        {line.discount_percent > 0 && <span className="text-xs ml-1">({line.discount_percent}%)</span>}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(line.tax_amount)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(line.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Payments */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Pagamentos ({payments.length})
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Método</TableHead>
                    <TableHead>Código Original</TableHead>
                    <TableHead className="text-right">Valor Bruto</TableHead>
                    <TableHead>Direção</TableHead>
                    <TableHead className="text-right">Efeito Económico</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((pay, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <PaymentMethodBadge method={pay.payment_method_normalized} code={pay.payment_code} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{pay.payment_code}</TableCell>
                      <TableCell className={`text-right ${pay.raw_amount < 0 ? 'text-red-600' : ''}`}>
                        {formatCurrency(pay.raw_amount)}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs ${pay.payment_direction === 'INFLOW' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {pay.payment_direction === 'INFLOW' ? '↓ Entrada' : '↑ Saída'}
                        </span>
                      </TableCell>
                      <TableCell className={`text-right font-medium ${pay.payment_direction === 'OUTFLOW' ? 'text-red-600' : 'text-emerald-600'}`}>
                        {pay.payment_direction === 'OUTFLOW' ? '-' : '+'}{formatCurrency(pay.economic_amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Reconciliation */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-700 mb-3">Reconciliação</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Total Documento</p>
                  <p className="font-semibold">{formatCurrency(doc.net_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Pagamentos</p>
                  <p className="font-semibold">{formatCurrency(doc.payment_total)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Diferença</p>
                  <p className={`font-semibold ${Math.abs(doc.reconciliation_difference) > 0.01 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(doc.reconciliation_difference)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Estado</p>
                  <ReconciliationBadge status={doc.reconciliation_status} />
                </div>
              </div>
            </div>
            
            {/* Metadata */}
            <div className="text-xs text-slate-400 space-y-1">
              <p>Ficheiro: {doc.source_file}</p>
              <p>Importado: {doc.imported_at}</p>
              <p>Batch: {doc.import_batch_id}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main component
const RetailVendas = () => {
  const { getStoreName } = useStoreMapping();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // View toggle: 'documents' or 'daily'
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'documents');
  
  // Use shared filter hook - default to 'this_month' for all retail pages
  const {
    selectedStore,
    setSelectedStore,
    period,
    setPeriod,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    startDate,
    endDate,
    filterProps,
  } = useStorePeriodFilter({ defaultPeriod: 'this_month', defaultStore: 'all' });
  
  // Page-specific filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data
  const [documents, setDocuments] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [dailyTotals, setDailyTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 20;
  
  // Detail modal
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Update URL when view mode changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (viewMode !== 'documents') {
      params.set('view', viewMode);
    } else {
      params.delete('view');
    }
    setSearchParams(params, { replace: true });
  }, [viewMode]);
  
  // Load documents (Por Documento view)
  useEffect(() => {
    if (viewMode !== 'documents') return;
    
    const loadDocuments = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const storeCode = selectedStore === 'all' ? '' : selectedStore;
        
        if (startDate === endDate) {
          const res = await axios.get(`${API}/retail-analytics/documents/${storeCode || 'all'}/${startDate}`);
          let docs = res.data.documents || [];
          
          if (typeFilter !== 'all') {
            docs = docs.filter(d => d.document_type === typeFilter);
          }
          
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            docs = docs.filter(d => 
              d.document_key.toLowerCase().includes(term) ||
              d.document_number.includes(term)
            );
          }
          
          setDocuments(docs);
        } else {
          const params = new URLSearchParams();
          params.set('start_date', startDate);
          params.set('end_date', endDate);
          if (storeCode) params.set('store_code', storeCode);
          
          const res = await axios.get(`${API}/retail-analytics/documents-range?${params.toString()}`);
          let docs = res.data.documents || [];
          
          if (typeFilter !== 'all') {
            docs = docs.filter(d => d.document_type === typeFilter);
          }
          
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            docs = docs.filter(d => 
              d.document_key.toLowerCase().includes(term) ||
              d.document_number.includes(term)
            );
          }
          
          setDocuments(docs);
        }
      } catch (err) {
        console.error('Error loading documents:', err);
        setError('Erro ao carregar documentos');
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadDocuments();
  }, [selectedStore, startDate, endDate, typeFilter, searchTerm, viewMode]);
  
  // Load daily aggregated data (Por Dia view)
  useEffect(() => {
    if (viewMode !== 'daily') return;
    
    const loadDailyData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const storeCode = selectedStore === 'all' ? '' : selectedStore;
        const params = new URLSearchParams();
        params.set('start_date', startDate);
        params.set('end_date', endDate);
        if (storeCode) params.set('store_code', storeCode);
        
        const res = await axios.get(`${API}/retail-analytics/daily-aggregated?${params.toString()}`);
        // Backend returns days sorted descending (most recent first)
        // Sort ascending for cumulative calculation, then reverse back
        const daysAsc = [...(res.data.days || [])].sort((a, b) => a.business_date.localeCompare(b.business_date));
        
        // Calculate cumulative
        let cumulative = 0;
        const daysWithCumulative = daysAsc.map(d => {
          cumulative += (d.net_sales || 0);
          return { ...d, cumulative: Math.round(cumulative * 100) / 100 };
        });
        
        // Reverse back to most recent first for display
        setDailyData(daysWithCumulative.reverse());
        setDailyTotals(res.data.totals || null);
      } catch (err) {
        console.error('Error loading daily data:', err);
        setError('Erro ao carregar dados diários');
        setDailyData([]);
        setDailyTotals(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadDailyData();
  }, [selectedStore, startDate, endDate, viewMode]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedStore, startDate, endDate, typeFilter, searchTerm, viewMode]);
  
  // Pagination for documents view
  const totalPages = Math.ceil(documents.length / pageSize);
  const paginatedDocs = documents.slice((page - 1) * pageSize, page * pageSize);
  
  // Pagination for daily view
  const dailyTotalPages = Math.ceil(dailyData.length / pageSize);
  const paginatedDaily = dailyData.slice((page - 1) * pageSize, page * pageSize);
  
  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc.document_key);
    setDetailOpen(true);
  };
  
  // Drill-down: click on a day to see documents for that day
  const handleDayClick = (day) => {
    // Use navigate for full page navigation to avoid state sync issues
    const params = new URLSearchParams();
    
    // Preserve store selection
    if (selectedStore && selectedStore !== 'all') {
      params.set('store', selectedStore);
    }
    
    // Set date filter to specific day
    params.set('period', 'custom');
    params.set('start', day.business_date);
    params.set('end', day.business_date);
    
    // Use navigate for a clean navigation
    navigate(`/lojas/vendas?${params.toString()}`);
  };
  
  const handleRefresh = () => {
    setLoading(true);
    window.location.reload();
  };
  
  // Summary stats for documents view
  const stats = {
    fts: documents.filter(d => ['FTS', 'FTV', 'FTN'].includes(d.document_type)).length,
    ncvs: documents.filter(d => d.document_type === 'NCVS').length,
    reconciled: documents.filter(d => d.reconciliation_status === 'RECONCILED').length,
    total: documents.reduce((sum, d) => sum + (d.net_amount || 0), 0),
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="retail-vendas">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Vendas
              </h1>
              <p className="text-sm text-slate-500 mt-1">Exploração de documentos de venda</p>
            </div>
            
            <Link to="/lojas">
              <Button variant="outline" size="sm">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Shared Store/Period Filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <StorePeriodFilter
                {...filterProps}
                onRefresh={handleRefresh}
                loading={loading}
                showAllStores={true}
              />
            </div>
            
            {/* View Toggle - Tabs */}
            <div className="flex flex-wrap items-center gap-4 pt-3 border-t">
              <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
                <TabsList className="grid w-[280px] grid-cols-2">
                  <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="view-toggle-documents">
                    <List className="w-4 h-4" />
                    Por Documento
                  </TabsTrigger>
                  <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="view-toggle-daily">
                    <CalendarDays className="w-4 h-4" />
                    Por Dia
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Page-specific filters (only for documents view) */}
              {viewMode === 'documents' && (
                <>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-500" />
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-[130px]" data-testid="type-filter">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="FTS">Vendas</SelectItem>
                        <SelectItem value="NCVS">Devoluções</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input 
                        placeholder="Pesquisar documento..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="document-search"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Single day filter indicator - shown when drilled down to a specific day */}
            {viewMode === 'documents' && period === 'custom' && startDate === endDate && (
              <div className="flex items-center gap-3 mt-3 pt-3 border-t bg-blue-50 -mx-4 px-4 py-2 rounded-b">
                <CalendarDays className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800 font-medium">
                  Documentos de {formatDate(startDate)}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-blue-600 hover:text-blue-800 h-7 px-2"
                  onClick={() => {
                    // Reset state and clear URL params
                    setPeriod('this_month');
                    setCustomStartDate(null);
                    setCustomEndDate(null);
                    
                    // Also clean up URL to reflect the reset
                    const params = new URLSearchParams();
                    if (selectedStore && selectedStore !== 'all') {
                      params.set('store', selectedStore);
                    }
                    setSearchParams(params, { replace: true });
                  }}
                  data-testid="clear-day-filter"
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Limpar filtro
                </Button>
              </div>
            )}
            
            {/* Summary - different for each view */}
            {viewMode === 'documents' && (
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Total:</span>
                  <span className="font-semibold">{documents.length} docs</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-emerald-600">Vendas:</span>
                  <span className="font-semibold">{stats.fts}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600">Devoluções:</span>
                  <span className="font-semibold">{stats.ncvs}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Reconciliados:</span>
                  <span className="font-semibold">{stats.reconciled}/{documents.length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Valor:</span>
                  <span className="font-semibold">{formatCurrency(stats.total)}</span>
                </div>
              </div>
            )}
            
            {viewMode === 'daily' && dailyTotals && (
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Período:</span>
                  <span className="font-semibold">{dailyTotals.days_with_data} dias</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-emerald-600">Vendas Líquidas:</span>
                  <span className="font-semibold">{formatCurrency(dailyTotals.net_sales)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600">Devoluções:</span>
                  <span className="font-semibold">{formatCurrency(dailyTotals.returns_value)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Transações:</span>
                  <span className="font-semibold">{formatNumber(dailyTotals.sales_documents)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Ticket Médio:</span>
                  <span className="font-semibold">{formatCurrency(dailyTotals.average_ticket)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Error */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-4">
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}
        
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
          </div>
        )}
        
        {/* Documents table (Por Documento view) */}
        {!loading && viewMode === 'documents' && documents.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Loja</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Reconciliação</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDocs.map((doc) => (
                    <TableRow 
                      key={doc.document_key} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleDocumentClick(doc)}
                      data-testid={`doc-row-${doc.document_key}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{formatDateTime(doc.business_date, doc.business_time)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{getStoreName(doc.store_code)}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {doc.raw_series}/{doc.document_number}
                      </TableCell>
                      <TableCell>
                        <DocumentTypeBadge type={doc.document_type} />
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(doc.net_amount)}
                      </TableCell>
                      <TableCell>
                        <ReconciliationBadge status={doc.reconciliation_status} />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <FileText className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-500">
                    Página {page} de {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Daily aggregated table (Por Dia view) */}
        {!loading && viewMode === 'daily' && dailyData.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Vendas Brutas</TableHead>
                    <TableHead className="text-right">Descontos</TableHead>
                    <TableHead className="text-right">Devoluções</TableHead>
                    <TableHead className="text-right">Vendas Líquidas</TableHead>
                    <TableHead className="text-right">
                      <span className="text-indigo-600">Acumulado</span>
                    </TableHead>
                    <TableHead className="text-right">Transações</TableHead>
                    <TableHead className="text-right">Ticket Médio</TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Banknote className="w-3 h-3 text-emerald-600" />
                        Numerário
                      </span>
                    </TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <CreditCard className="w-3 h-3 text-blue-600" />
                        Cartão
                      </span>
                    </TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDaily.map((day) => (
                    <TableRow 
                      key={day.business_date} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleDayClick(day)}
                      data-testid={`day-row-${day.business_date}`}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{formatDate(day.business_date)}</span>
                          <span className="text-xs text-slate-400">{day.business_date}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(day.gross_sales)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-amber-600">
                        {formatCurrency(day.discounts_total)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {formatCurrency(day.returns_value)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {formatCurrency(day.net_sales)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-indigo-600">
                        {formatCurrency(day.cumulative)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(day.sales_documents)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(day.average_ticket)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">
                        {formatCurrency(day.net_cash)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-700">
                        {formatCurrency(day.net_card)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" title="Ver documentos deste dia">
                          <ArrowUpRight className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {dailyTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-500">
                    Página {page} de {dailyTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(dailyTotalPages, p + 1))}
                      disabled={page === dailyTotalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* No data */}
        {!loading && viewMode === 'documents' && documents.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sem documentos</h3>
              <p className="text-sm text-slate-500 mt-1">
                Não existem documentos para os filtros selecionados.
              </p>
            </CardContent>
          </Card>
        )}
        
        {!loading && viewMode === 'daily' && dailyData.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sem dados diários</h3>
              <p className="text-sm text-slate-500 mt-1">
                Não existem dados de vendas para o período selecionado.
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Document detail modal */}
        <DocumentDetailModal 
          documentKey={selectedDocument}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
        />
      </div>
    </div>
  );
};

export default RetailVendas;
